/**
 * Responsibility: Implement logicForge behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
import type {
  GameState,
  LogicUpdate,
  LogicForgeAIResponse,
  NarratorAIResponse,
  GenesisAIResponse,
  TraitShiftAIResponse,
  WorkerRequest,
  WorkerResponse,
  LogicForgeMeta,
  Companion,
  Ability,
  NPC,
  WorldTickUpdate,
  WorldEnrichment,
  ActionExecutionContext,
  WorldTaxonomy,
} from "@anyquest/shared-core"
import { ActionCategory } from "@anyquest/shared-core"
import { router } from './config'; // side effect: ensures providers are registered
import { extractJson } from './json-utils'
import {
  ACTION_VALIDATOR_SYSTEM,
  GENESIS_SYSTEM,
  VESSEL_GENESIS_SYSTEM,
  SCENARIO_GENESIS_SYSTEM,
  LOGIC_FORGE_SYSTEM,
  ABILITY_FORGE_SYSTEM,
  TRADE_SEED_SYSTEM,
  CHEAT_SYSTEM,
  buildAbilityForgePrompt,
  buildTradeSeedPrompt,
  buildNarratorHybridSystem,
  buildNarratorProseSystem,
  buildNarratorSuggestionsSystem,
  buildLogicForgePrompt,
  buildNarratorPrompt,
  buildCombatSummary,
  buildGenesisPrompt,
  buildCheatPrompt,
  TRAIT_SHIFT_SYSTEM,
  WORLD_TICK_SYSTEM,
  buildWorldTickPrompt,
  WORLD_ENRICH_SYSTEM,
  buildWorldEnrichPrompt,
  WORLD_FORGE_SYSTEM,
  buildWorldForgePrompt,
  type WorldForgePromptContext,
} from './prompts'
import { locateNarratorSeparator } from './narrator-hybrid'
import { normalizeNarratorPerspective } from './narrator-perspective'
import { buildActionCacheKey, buildActionEnvelope } from '../engine/action-contract'
import {
  parseGenesisAIResponse,
  parseTradeSeed,
  parseWorldForgeNodes,
  parseWorldTickUpdate,
} from '../engine/entities/forge-contract-artifact'
export type ActionValidatorResponse = { verdict: 'ok' | 'bad' }
// ============================================================
// LOGICFORGE.TS — Two-phase AI orchestration
// Phase 1: Logic Forge → logicFn string → Web Worker execution
// Phase 2: Narrator → prose + suggestions + optional newRoom
//
// runLogicPhase and narrator functions are exported separately so
// game-store.ts can process combat state between the two phases.
// All AI calls routed through src/ai/router.ts.
// ============================================================

const WORKER_TIMEOUT_MS = 12000
const NARRATOR_PERSPECTIVE_TAIL_LEN = 40
type LogicPhaseActionOptions = NonNullable<Parameters<typeof buildActionEnvelope>[2]>
let logicCacheRuntimePromise: Promise<typeof import('../engine/logic-cache')> | null = null
function loadLogicCacheRuntime() {
  if (!logicCacheRuntimePromise) logicCacheRuntimePromise = import('../engine/logic-cache')
  return logicCacheRuntimePromise
}

function createNarratorPerspectiveStreamer() {
  let tail = ''

  return (chunk: string, flush = false): string => {
    const source = `${tail}${String(chunk ?? '')}`
    if (!source) return ''
    if (flush) {
      tail = ''
      return normalizeNarratorPerspective(source)
    }
    const safeLen = Math.max(0, source.length - NARRATOR_PERSPECTIVE_TAIL_LEN)
    const safe = source.slice(0, safeLen)
    tail = source.slice(safeLen)
    return normalizeNarratorPerspective(safe)
  }
}

export async function runActionValidator(state: GameState, input: string): Promise<ActionValidatorResponse> {
  // We can use the logic forge prompt as it has all the context needed.
  const userPrompt = buildLogicForgePrompt(state, input)
  try {
    const res = await router.route<any>({
      jobType: 'action_validator',
      systemPrompt: ACTION_VALIDATOR_SYSTEM,
      userPrompt,
    })
    const parsed = typeof res === 'string' ? extractJson(res) : res
    return parsed as ActionValidatorResponse
  } catch (e) {
    console.warn('[AI] Action validator failed, defaulting to "ok"', e)
    return { verdict: 'ok' }; // Fail open
  }
}


export const FALLBACK_LOGIC: LogicUpdate = {
  successLevel: 0,
  updates: { timeCost: 5 },
}
export const FALLBACK_META: LogicForgeMeta = { cacheable: false, category: ActionCategory.other }
// Lazy-create a single Worker instance; replace on timeout
let worker: Worker | null = null
function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('../engine/logic.worker.ts', import.meta.url),
      { type: 'module' }
    )
  }
  return worker
}

function killWorker(): void {
  worker?.terminate()
  worker = null
}

/**
 * Execute any Logic Forge function in the sandboxed Web Worker.
 * Generic over the return type so both player (LogicUpdate) and
 * NPC (NpcLogicUpdate) functions can use the same infrastructure.
 */
export async function executeInWorker<T>(
  logicFnString: string,
  stateSnapshot: GameState,
  roll: number,
  fallback: T,
  npcMode = false,
  mechanics?: { dc: number; primaryStat: string; bonus: number; chance: number },
  executionContext?: ActionExecutionContext,
): Promise<T> {
  return new Promise((resolve) => {
    const w = getWorker()
    const timeout = setTimeout(() => {
      console.warn('[LogicForge] Worker timed out — killing and using fallback')
      killWorker()
      resolve(fallback)
    }, WORKER_TIMEOUT_MS)
    const handler = (event: MessageEvent<import("@anyquest/shared-core").WorkerResponse>) => {
      clearTimeout(timeout)
      w.removeEventListener('message', handler)
      resolve(event.data.success ? (event.data.result as T) : fallback)
    }
    w.addEventListener('message', handler)
    const request: import("@anyquest/shared-core").WorkerRequest = {
      logicFnString,
      stateSnapshot: structuredClone(stateSnapshot), // strip Proxy
      roll,
      npcMode,
      executionContext,
      mechanics,
    }
    w.postMessage(request)
  })
}

async function executeLogicInWorker(
  logicFnString: string,
  stateSnapshot: GameState,
  roll: number,
  mechanics?: { dc: number; primaryStat: string; bonus: number; chance: number },
): Promise<LogicUpdate> {
  return executeInWorker(logicFnString, stateSnapshot, roll, FALLBACK_LOGIC, false, mechanics)
}

// --- Staged Genesis: World -> Vessel -> Scenario ---

export async function runVesselGenesis(
  identity: string,
  bio: string,
  worldTaxonomy: WorldTaxonomy,
): Promise<Partial<GenesisAIResponse>> {
  const prompt = `World: ${worldTaxonomy.worldName}. Core Reality: ${worldTaxonomy.coreReality}.
Art Direction: ${worldTaxonomy.imageStyle}.
Economy: ${worldTaxonomy.economy.currencyName} (${worldTaxonomy.economy.currencySymbol}).
Vessel concept: ${identity}. Bio: ${bio}.
Generate the Vessel JSON (name, canonical identity, sex, stats, traits, skills, abilities, inventory, initial quests).`
  
  return await router.route<Partial<GenesisAIResponse>>({
    jobType: 'genesis',
    systemPrompt: VESSEL_GENESIS_SYSTEM,
    userPrompt: prompt,
  })
}

export async function runScenarioGenesis(
  vessel: Partial<GenesisAIResponse>,
  worldTaxonomy: WorldTaxonomy,
): Promise<Partial<GenesisAIResponse>> {
  const prompt = `World: ${worldTaxonomy.worldName}. Core Reality: ${worldTaxonomy.coreReality}.
Vessel: ${vessel.identity} (${vessel.name}).
Generate the starting Scenario JSON (room name, desc, narration, items, NPCs, and optionally starting companions).`

  return await router.route<Partial<GenesisAIResponse>>({
    jobType: 'genesis',
    systemPrompt: SCENARIO_GENESIS_SYSTEM,
    userPrompt: prompt,
  })
}

export async function runGenesis(
  identity: string,
  bio: string,
  worldSeed: string,
  worldTaxonomy: WorldTaxonomy, // Stage 1 result
): Promise<GenesisAIResponse> {
  // Stage 2: Vessel Manifestation
  let vesselResult: Partial<GenesisAIResponse> | null = null
  try {
    vesselResult = await runVesselGenesis(identity, bio, worldTaxonomy)
  } catch (err) {
    console.warn('[Genesis] Vessel manifestation failed, using empty fallback:', err)
    vesselResult = {}
  }
  
  // Stage 3: Scenario Initiation
  let scenarioResult: Partial<GenesisAIResponse> | null = null
  try {
    scenarioResult = await runScenarioGenesis(vesselResult || {}, worldTaxonomy)
  } catch (err) {
    console.warn('[Genesis] Scenario initiation failed, using empty fallback:', err)
    scenarioResult = {}
  }

  const result = {
    ...vesselResult,
    ...scenarioResult,
  }

  const parsed = parseGenesisAIResponse(result)
  if (!parsed) {
    console.error('[Genesis] Final payload validation failed. Merged result:', result)
    throw new Error('Genesis returned an invalid payload. The AI response did not match the expected structure.')
  }
  return {
    name: typeof parsed.name === 'string' ? parsed.name : '',
    identity: typeof parsed.identity === 'string' ? parsed.identity : '',
    sex: typeof parsed.sex === 'string' ? parsed.sex : '',
    stats: {
      STR: parsed.stats?.STR ?? 10,
      DEX: parsed.stats?.DEX ?? 10,
      CON: parsed.stats?.CON ?? 10,
      INT: parsed.stats?.INT ?? 10,
      WIS: parsed.stats?.WIS ?? 10,
      CHA: parsed.stats?.CHA ?? 10,
      PER: parsed.stats?.PER ?? 10,
      LCK: parsed.stats?.LCK ?? 10,
      WIL: parsed.stats?.WIL ?? 10,
      FOR: parsed.stats?.FOR ?? 10,
      AGI: parsed.stats?.AGI ?? 10,
      PRS: parsed.stats?.PRS ?? 10,
    },
    traits: Array.isArray(parsed.traits) ? parsed.traits : [],
    skills: Array.isArray(parsed.skills) ? parsed.skills : [],
    abilities: Array.isArray(parsed.abilities) ? parsed.abilities : [],
    inventory: Array.isArray(parsed.inventory) ? parsed.inventory : [],
    quests: Array.isArray(parsed.quests) ? parsed.quests : [],
    companions: Array.isArray(parsed.companions)
      ? parsed.companions.map(companion => ({
          ...companion,
          skills: Array.isArray(companion?.skills) ? companion.skills : [],
          inventory: Array.isArray(companion?.inventory) ? companion.inventory : [],
        }))
      : [],
    room: {
      name: parsed.room?.name ?? 'The Void',
      desc: parsed.room?.desc ?? '',
      narration: parsed.room?.narration ?? parsed.room?.desc ?? '',
      items: Array.isArray(parsed.room?.items) ? parsed.room.items : [],
      npcs: Array.isArray(parsed.room?.npcs)
        ? parsed.room.npcs.map(npc => ({
            ...npc,
            inventory: Array.isArray(npc?.inventory) ? npc.inventory : [],
          }))
        : [],
      suggestions: Array.isArray(parsed.room?.suggestions) ? parsed.room.suggestions : [],
    },
  }
}

// --- Phase 1: Logic Forge — returns logicUpdate + meta ---

export interface LogicPhaseResult {
  logicUpdate: LogicUpdate
  meta: LogicForgeMeta
  logicFnString: string
  mechanics?: {
    dc: number
    primaryStat: string
    bonus: number
    chance: number
  }
}

/**
 * Specifically forge a logic function for a named ability.
 * Caches directly on the Ability object in the game state.
 */
export async function abilityForge(ability: Ability, state: GameState): Promise<string> {
  const result = await router.route<{ logicFn: string }>({
    jobType: 'logic_forge',
    systemPrompt: ABILITY_FORGE_SYSTEM,
    userPrompt: buildAbilityForgePrompt(ability, state),
  })
  return result.logicFn ?? '(state, roll) => ({ successLevel: 0, updates: { timeCost: 5 } })'
}

/**
 * Get an initial trade offer from an NPC.
 */
export async function getNpcTradeSeed(npc: NPC, state: GameState): Promise<{ offer: string, worth: number }> {
  const result = await router.route<{ offer: string, worth?: number }>({
    jobType: 'logic_forge',
    systemPrompt: TRADE_SEED_SYSTEM,
    userPrompt: buildTradeSeedPrompt(npc, state),
  })

  const parsed = parseTradeSeed(result)
  let finalOffer = String(parsed?.offer || '').trim()
  let finalWorth = Number(parsed?.worth ?? 0)

  // Validate NPC actually has the item
  if (finalOffer && Array.isArray(npc.inventory) && npc.inventory.length > 0) {
    const hasItem = npc.inventory.some(i => i.toLowerCase() === finalOffer.toLowerCase())
    if (!hasItem) {
      // Fallback: Use the first item in their inventory if they hallucinate an item
      finalOffer = npc.inventory[0]
    }
  }

  // If no worth provided or too low, give it a baseline (1 ration cost)
  if (finalWorth <= 0) {
    const rationCost = state.world.taxonomy?.economy?.rationCost ?? 10
    finalWorth = rationCost
  }

  return { 
    offer: finalOffer || 'nothing of interest', 
    worth: finalWorth 
  }
}

/**
 * Execute a dev-mode cheat command. Returns a LogicUpdate that applies the requested state change.
 * Sanitizes AI-generated JS by extracting and validating only the return object shape.
 */
export async function runCheatCommand(prompt: string, state: GameState): Promise<LogicUpdate> {
  const result = await router.route<{ logicFn: string }>({
    jobType: 'cheat',
    systemPrompt: CHEAT_SYSTEM,
    userPrompt: buildCheatPrompt(prompt, state),
  })
  let logicFnString = '(state, roll) => ({ successLevel: 2, updates: { timeCost: 1 } })'
  if (result.logicFn) {
    try {
      // Sanitize: extract only the return object and rebuild a safe function
      // Match pattern: (state, roll) => { return {...} } or (state, roll) => {...}
      const fnMatch = result.logicFn.match(/\(state,\s*roll\)\s*=>\s*(?:\{\s*return\s+)?({[\s\S]*})(?:\s*\})?/)
      if (fnMatch && fnMatch[1]) {
        const returnObj = fnMatch[1].trim()
        // Validate it's a valid JSON-like object literal (basic check)
        if (returnObj.startsWith('{') && returnObj.endsWith('}')) {
          logicFnString = `(state, roll) => ${returnObj}`
        }
      }
    } catch {
      // Fall back to safe default on parse error
    }
  }
  return executeInWorker(logicFnString, state, Math.ceil(Math.random() * 20), FALLBACK_LOGIC, false)
}

export async function runLogicPhase(
  state: GameState,
  input: string,
  actorName?: string,
  actionOptions: Partial<LogicPhaseActionOptions> = {},
): Promise<LogicPhaseResult> {
  const { getCachedEntry, cacheEntry } = await loadLogicCacheRuntime()
  const actionEnvelope = buildActionEnvelope(state, input, actorName
    ? {
        source: 'companion',
        actor: { kind: 'companion', name: actorName },
        ...actionOptions,
      }
    : {
        source: 'player',
        actor: { kind: 'player' },
        ...actionOptions,
      })
  const cacheKey = buildActionCacheKey(state, actionEnvelope)
  const cached = getCachedEntry(cacheKey)
  let logicFnString: string
  let meta: LogicForgeMeta
  // Respect cacheability contract: dynamic/non-cacheable actions must be re-forged.
  if (cached?.meta?.cacheable) {
    logicFnString = cached.fn
    meta = cached.meta
    console.debug(`[LogicForge] Cache hit (${meta.category}, cacheable=${meta.cacheable}):`, cacheKey.slice(0, 80))
  } else {
    if (cached && !cached.meta?.cacheable) {
      console.debug('[LogicForge] Ignoring stale non-cacheable cache entry:', cacheKey.slice(0, 80))
    }
    const logicResponse = await router.route<LogicForgeAIResponse>({
      jobType: 'logic_forge',
      systemPrompt: LOGIC_FORGE_SYSTEM,
      userPrompt: buildLogicForgePrompt(state, input, actorName, actionEnvelope),
    })
    logicFnString = logicResponse.logicFn
      ?? '(state, roll) => ({ successLevel: 0, updates: { timeCost: 5 } })'
    meta = logicResponse.meta ?? FALLBACK_META
    const mechanics = logicResponse.mechanics
    if (meta.cacheable) cacheEntry(cacheKey, logicFnString, meta).catch(console.warn)
    const roll = Math.floor(Math.random() * 20) + 1
    const logicUpdate = await executeLogicInWorker(logicFnString, state, roll, mechanics)
    return { logicUpdate, meta, mechanics, logicFnString }
  }

  const roll = Math.floor(Math.random() * 20) + 1
  const logicUpdate = await executeLogicInWorker(logicFnString, state, roll)
  return { logicUpdate, meta, logicFnString }
}

// --- Phase 2: Narrator (Hybrid Streaming) ---
//
// streamNarratorPhase is the primary narrator entry point.
// It streams prose word-by-word while accumulating the structured JSON
// that follows the "===" separator. Once the stream ends, onComplete
// is called with the parsed suggestions + optional newRoom data.
//
// Format the AI emits:
//   [prose text…]
//   ===
//   {"suggestions":[…],"newRoom":null|{…}}

export async function* streamNarratorPhase(
  state: GameState,
  input: string,
  logicUpdate: LogicUpdate,
  onComplete: (data: Omit<NarratorAIResponse, 'prose'>) => void,
): AsyncGenerator<string> {
  const combatSummary = buildCombatSummary(state.combat)
  const stream = router.stream({
    jobType: 'narrator',
    systemPrompt: buildNarratorHybridSystem(),
    userPrompt: buildNarratorPrompt(state, input, logicUpdate, combatSummary),
  })
  // Detect separator lines like "===" and recover from model drift to "==".
  // Keep a larger tail to handle boundary splits across chunk edges.
  const tailLen = 8
  let tail = ''
  let seenSep = false
  let jsonAcc = ''
  const normalizeNarratorChunk = createNarratorPerspectiveStreamer()

  for await (const chunk of stream) {
    if (seenSep) {
      jsonAcc += chunk
      continue
    }

    const scan = tail + chunk
    const sep = locateNarratorSeparator(scan)
    
    // Recovery for models that omit === and go straight to JSON {"
    // We look for the start of the suggestions object
    const jsonStartIdx = scan.indexOf('{"')

    if (sep) {
      seenSep = true
      const proseChunk = scan.slice(0, sep.index)
      const normalized = normalizeNarratorChunk(proseChunk, true)
      if (normalized) yield normalized
      jsonAcc = scan.slice(sep.index + sep.length)
      tail = ''
    } else if (jsonStartIdx !== -1) {
      seenSep = true
      const proseChunk = scan.slice(0, jsonStartIdx)
      const normalized = normalizeNarratorChunk(proseChunk, true)
      if (normalized) yield normalized
      jsonAcc = scan.slice(jsonStartIdx)
      tail = ''
    } else {
      // Find the last safe split point (whitespace) within the scan minus tail margin
      const safeEnd = Math.max(0, scan.length - 32)
      const lastSpace = scan.lastIndexOf(' ', safeEnd)
      const splitIdx = lastSpace !== -1 ? lastSpace + 1 : safeEnd

      const safe = scan.slice(0, splitIdx)
      const normalized = normalizeNarratorChunk(safe, false)
      if (normalized) yield normalized
      tail = scan.slice(splitIdx)
    }
  }

  // Flush any remaining prose if separator was never found
  if (!seenSep && tail) {
    const normalized = normalizeNarratorChunk(tail, true)
    if (normalized) yield normalized
  }

  // Parse the structured JSON section
  let narratorData: Omit<NarratorAIResponse, 'prose'> = { suggestions: [] }
  const rawJson = jsonAcc.trim()
  if (rawJson) {
    try {
      const parsed = extractJson(rawJson) as any
      let newRoom = parsed.newRoom ?? undefined
      if (newRoom) {
        if (newRoom.xOff !== undefined && newRoom.xOff !== null) {
          newRoom.xOff = parseInt(String(newRoom.xOff), 10) || 0
        }
        if (newRoom.yOff !== undefined && newRoom.yOff !== null) {
          newRoom.yOff = parseInt(String(newRoom.yOff), 10) || 0
        }
        if (newRoom.zOff !== undefined && newRoom.zOff !== null) {
          newRoom.zOff = parseInt(String(newRoom.zOff), 10) || 0
        }
      }
      narratorData = {
        suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions : [],
        newRoom,
        atlasHints: parsed.atlasHints ?? undefined,
      }
    } catch { /* empty suggestions — acceptable */ }
  }

  onComplete(narratorData)
}

/** Non-streaming narrator call — collects the full hybrid response then returns. */
export async function runNarratorPhase(
  state: GameState,
  input: string,
  logicUpdate: LogicUpdate,
): Promise<NarratorAIResponse> {
  let prose = ''
  let structuredData: Omit<NarratorAIResponse, 'prose'> = { suggestions: [] }
  for await (const chunk of streamNarratorPhase(
    state, input, logicUpdate, d => { structuredData = d; },
  )) {
    prose += chunk
  }

  return { prose: prose.trim(), ...structuredData }
}

// --- Legacy helpers (kept for backward-compat, prefer streamNarratorPhase) ---

export function streamNarratorProse(
  state: GameState,
  input: string,
  logicUpdate: LogicUpdate,
): AsyncGenerator<string> {
  const combatSummary = buildCombatSummary(state.combat)
  return router.stream({
    jobType: 'narrator',
    systemPrompt: buildNarratorProseSystem(),
    userPrompt: buildNarratorPrompt(state, input, logicUpdate, combatSummary),
  })
}

export async function getNarratorData(
  state: GameState,
  input: string,
  logicUpdate: LogicUpdate,
): Promise<Omit<NarratorAIResponse, 'prose'>> {
  const combatSummary = buildCombatSummary(state.combat)
  const narratorResponse = await router.route<Omit<NarratorAIResponse, 'prose'>>({
    jobType: 'narrator',
    systemPrompt: buildNarratorSuggestionsSystem(),
    userPrompt: buildNarratorPrompt(state, input, logicUpdate, combatSummary),
  })
  return {
    suggestions: narratorResponse.suggestions ?? [],
    newRoom: narratorResponse.newRoom,
    atlasHints: narratorResponse.atlasHints,
  }
}

// --- Companion chat: AI-generated in-character response ---

export async function runCompanionChat(
  gs: GameState,
  companion: Companion,
  playerMessage: string,
): Promise<string> {
  const recentLines = (companion.conversations ?? [])
    .slice(-6)
    .map(e => `${e.from}: ${e.text}`)
    .join('\n')
  const feelingsSummary = (companion.feelings || [])
    .map(f => `${f.sentiment} towards ${f.target} (${f.intensity}/10)`)
    .join('; ')
  const motivesSummary = (companion.motives || [])
    .map(m => `[${m.priority}] ${m.desc}`)
    .join('; ')
  const separated = companion.separated && companion.location
  const separationCtx = separated
    ? `You are currently separated from the party and not physically present. Communication is distant or recalled. `
    : ''

  const systemPrompt =
    `You are ${companion.name}, ${companion.identity}. Bio: "${companion.bio}". ` +
    `Current motives: ${motivesSummary || 'none'}. ` +
    `Feelings: ${feelingsSummary || 'neutral'}. ` +
    separationCtx +
    `You are talking to ${gs.player.identity}. Stay in character. ` +
    `Respond naturally in 1–3 sentences. Plain prose only — no JSON, no markdown.`
  const userPrompt = recentLines
    ? `Recent exchange:\n${recentLines}\n\n${gs.player.identity} says: "${playerMessage}"\n\n${companion.name} replies:`
    : `${gs.player.identity} says: "${playerMessage}"\n\n${companion.name} replies:`
  const stream = router.stream({
    jobType: 'narrator',
    systemPrompt,
    userPrompt,
  })
  let response = ''
  for await (const chunk of stream) response += chunk
  return response.trim() || '...'
}

// --- Combined action (non-streaming convenience wrapper) ---

export interface ActionResult {
  logicUpdate: LogicUpdate
  narratorResponse: NarratorAIResponse
  meta: LogicForgeMeta
}

export async function runAction(state: GameState, input: string): Promise<ActionResult> {
  const { logicUpdate, meta } = await runLogicPhase(state, input)
  const narratorResponse = await runNarratorPhase(state, input, logicUpdate)
  return { logicUpdate, meta, narratorResponse }
}

// --- World Simulation: Tick ---

const VALID_ATTR_KEYS = new Set(['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA', 'PER', 'LCK', 'WIL', 'FOR', 'AGI', 'PRS'])
function cleanShortLabel(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') return fallback
  const text = raw.trim().replace(/\s+/g, ' ').slice(0, 42)
  return text || fallback
}

function sanitizeNpcUpdates(raw: unknown): NonNullable<WorldTickUpdate['npcUpdates']> {
  if (!Array.isArray(raw)) return []
  const seenIds = new Set<string>()
  const updates: NonNullable<WorldTickUpdate['npcUpdates']> = []
  let moveCount = 0
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as Record<string, unknown>
    const id = typeof e.id === 'string' ? e.id.trim() : ''
    if (!id || seenIds.has(id)) continue
    seenIds.add(id)
    const update: NonNullable<WorldTickUpdate['npcUpdates']>[number] = { id }
    if (typeof e.moveTo === 'string' && /^-?\d+_-?\d+$/.test(e.moveTo.trim())) {
      if (moveCount < 2) {
        update.moveTo = e.moveTo.trim()
        moveCount++
      }
    }
    if (Array.isArray(e.intentions)) {
      update.intentions = e.intentions
        .filter((v): v is string => typeof v === 'string')
        .map(v => v.trim())
        .filter(Boolean)
        .slice(0, 3)
    }
    if (typeof e.relationship === 'number' && Number.isFinite(e.relationship)) {
      update.relationship = Math.max(-100, Math.min(100, Math.round(e.relationship)))
    }
    if (typeof e.memoryAdd === 'string') {
      const mem = e.memoryAdd.trim().slice(0, 180)
      if (mem) update.memoryAdd = mem
    }
    if (Array.isArray(e.activeEffects)) {
      const effects = [...new Set(
        e.activeEffects
          .filter((v): v is string => typeof v === 'string')
          .map(v => cleanShortLabel(v, 'EFFECT'))
      )].slice(0, 6)
      if (effects.length) update.activeEffects = effects
    }
    if (e.dormantEffectsAdd && typeof e.dormantEffectsAdd === 'object') {
      const d = e.dormantEffectsAdd as Record<string, unknown>
      const label = cleanShortLabel(d.label, 'Dormant Effect')
      const source = cleanShortLabel(d.source, 'world_tick')
      const attrRaw = typeof d.attr === 'string' ? d.attr.trim().toUpperCase() : ''
      update.dormantEffectsAdd = {
        label,
        source,
        ...(VALID_ATTR_KEYS.has(attrRaw) ? { attr: attrRaw as 'STR' | 'DEX' | 'CON' | 'INT' | 'WIS' | 'CHA' | 'PER' | 'LCK' | 'WIL' | 'FOR' | 'AGI' | 'PRS' } : {}),
      }
    }

    updates.push(update)
  }

  return updates.slice(0, 8)
}

export async function runWorldTick(state: GameState, triggeredSpawns: string[] = []): Promise<WorldTickUpdate> {
  const result = await router.route<WorldTickUpdate>({
    jobType: 'world_tick',
    systemPrompt: WORLD_TICK_SYSTEM,
    userPrompt: buildWorldTickPrompt(state, triggeredSpawns),
  })
  const parsed = parseWorldTickUpdate(result)
  if (!parsed) return {}
  const worldEvents = Array.isArray(parsed.worldEvents)
    ? parsed.worldEvents
        .filter((e): e is string => typeof e === 'string')
        .map(e => e.trim())
        .filter(Boolean)
        .slice(0, 6)
    : []
  return {
    clockUpdates:    parsed.clockUpdates,
    npcUpdates:      sanitizeNpcUpdates(parsed.npcUpdates),
    newNpcs:         parsed.newNpcs,
    newItems:        parsed.newItems,
    questUpdates:    Array.isArray(parsed.questUpdates) ? parsed.questUpdates : [],
    newNodes:        Array.isArray(parsed.newNodes) ? parsed.newNodes.slice(0, 2) : [],
    worldEvents,
    roomFlagUpdates: Array.isArray(parsed.roomFlagUpdates) ? parsed.roomFlagUpdates : [],
  }
}

// --- World Simulation: Enrichment ---

export async function runWorldEnrichment(state: GameState): Promise<WorldEnrichment> {
  const result = await router.route<WorldEnrichment>({
    jobType: 'world_enrich',
    systemPrompt: WORLD_ENRICH_SYSTEM,
    userPrompt: buildWorldEnrichPrompt(state),
  })
  return {
    narrative:       result.narrative ?? '',
    newQuests:       Array.isArray(result.newQuests) ? result.newQuests : [],
    npcEnrichments:  Array.isArray(result.npcEnrichments) ? result.npcEnrichments : [],
  }
}

// --- Entropy Shift: Reshuffle traits ---

export async function runTraitShift(bio: string): Promise<TraitShiftAIResponse> {
  const result = await router.route<TraitShiftAIResponse>({
    jobType: 'trait_shift',
    systemPrompt: TRAIT_SHIFT_SYSTEM,
    userPrompt: `Character bio: "${bio}"`,
  })
  return {
    traits: Array.isArray(result.traits) ? result.traits.slice(0, 3) : [],
  }
}

// --- World Forge: batch-enrich multiple atlas nodes in a single AI call ---

export interface WorldForgeNode {
  key: string
  name: string
  desc: string
  items: string[]
  npcs: Array<{
    name: string
    identity: string
    lore: string
    sex?: string
    intentions: string[]
    relationship: number
    memory: string[]
    inventory: string[]
  }>
  spawns?: Array<{
    archetype: string
    level: number
    chancePerHour: number
    lootInfo: string
    friendliness: number
    alertness: string
  }>
}

export async function runWorldForge(
  worldSeed: string,
  startingRoomName: string,
  nodeKeys: string[],
  context?: WorldForgePromptContext,
): Promise<WorldForgeNode[]> {
  if (!nodeKeys.length) return []
  
  const CHUNK_SIZE = 10
  const allGenerated: WorldForgeNode[] = []
  
  for (let i = 0; i < nodeKeys.length; i += CHUNK_SIZE) {
    const chunk = nodeKeys.slice(i, i + CHUNK_SIZE)
    const prompt = buildWorldForgePrompt(worldSeed, startingRoomName, chunk, context)
    const result = await router.route<{ nodes: WorldForgeNode[] }>({
      jobType: 'world_forge',
      systemPrompt: WORLD_FORGE_SYSTEM,
      userPrompt: prompt,
    })

    const sanitized = parseWorldForgeNodes(result)
    if (Array.isArray(sanitized)) {
      allGenerated.push(...sanitized)
    }
  }
  
  return allGenerated
}
